import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  CheckCircle,
  BadgeCheck,
  Landmark,
  ShieldCheck,
  Clock3,
  BriefcaseBusiness,
} from 'lucide-react';
import HeroSection from '../components/HeroSection';

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6 } } };

const industries = [
  'Restaurants & Food Service', 'Retail', 'Construction', 'Medical & Dental',
  'Auto Repair', 'Beauty & Wellness', 'Transportation', 'Legal Services',
  'Real Estate', 'Technology', 'Manufacturing', 'Education', 'Agriculture',
  'Hospitality', 'Fitness & Gyms', 'HVAC & Trades', 'Other',
];

const amounts = ['$5,000 – $10,000', '$10,000 – $25,000', '$25,000 – $50,000',
  '$50,000 – $100,000', '$100,000 – $250,000', '$250,000 – $500,000'];

const revenues = ['Under $10,000/mo', '$10,000 – $25,000/mo', '$25,000 – $50,000/mo',
  '$50,000 – $100,000/mo', '$100,000 – $250,000/mo', '$250,000+/mo'];

const benefits = [
  {
    icon: <BadgeCheck size={18} className="text-amber-500" />,
    text: 'Decisions in as little as 4 hours'
  },
  {
    icon: <Clock3 size={18} className="text-amber-500" />,
    text: 'Funds deposited within 24 hours'
  },
  {
    icon: <ShieldCheck size={18} className="text-amber-500" />,
    text: 'No hidden fees, fully transparent'
  },
  {
    icon: <BriefcaseBusiness size={18} className="text-amber-500" />,
    text: 'Dedicated funding advisor'
  },
];

const inputClass =
  'w-full px-4 py-3 rounded-2xl border border-gray-200 bg-white text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400 transition-shadow shadow-[0_2px_10px_rgba(15,23,42,0.03)]';

export default function Apply() {
  const [form, setForm] = useState({
    businessName: '', ownerName: '', email: '', phone: '',
    industry: '', monthlyRevenue: '', fundingAmount: '', useOfFunds: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handle = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  const submit = async e => {
    e.preventDefault();

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const response = await fetch('/api/funding-application', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(form),
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        throw new Error(payload?.error || 'Server error while submitting your application.');
      }

      setSubmitted(true);
    } catch (error) {
      console.error('Funding form submission failed:', error);
      setSubmitError('We were unable to send your application at this time. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="bg-[#F8F7F4] text-gray-900">
      <HeroSection
        tag="Apply for Funding"
        title="Get the Capital Your<br/>Business Deserves"
        subtitle="Complete the form below. Most applications take under 5 minutes, and decisions arrive in hours."
        image="/images/offline/photo-1507679799987-c73779587ccf.jpg"
      />

      <section className="page-section bg-[#F8F7F4]">
        <div className="page-container">
          <div className="grid gap-8 lg:grid-cols-5 lg:gap-10 xl:gap-12">
            {/* Sidebar */}
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="space-y-6 lg:col-span-2 md:space-y-8"
            >
              <div className="rounded-[28px] border border-gray-200 bg-white p-6 shadow-[0_10px_35px_rgba(15,23,42,0.06)] md:p-7">
                <h3 className="font-bold text-gray-900 mb-5 text-lg">Why Apply with Bizfund</h3>
                <ul className="space-y-4">
                  {benefits.map((b, i) => (
                    <li key={i} className="flex items-center gap-3 text-sm text-gray-700">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#0F172A]">
                        {b.icon}
                      </span>
                      {b.text}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-[28px] overflow-hidden border border-gray-200 shadow-[0_10px_35px_rgba(15,23,42,0.06)] bg-white">
                <img
                  src="/images/offline/photo-1519389950473-47ba0277781c.jpg"
                  alt="Business team"
                  className="w-full h-56 object-cover"
                />
              </div>

              <div className="relative overflow-hidden rounded-[28px] border border-white/10 bg-[#0F172A] p-6 text-white shadow-[0_18px_45px_rgba(15,23,42,0.20)] md:p-7">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(251,191,36,0.14),transparent_30%),radial-gradient(circle_at_bottom_left,rgba(251,191,36,0.08),transparent_24%)]" />
                <div className="relative">
                  <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10 border border-white/10 mb-4">
                    <Landmark size={22} className="text-amber-400" />
                  </div>
                  <div className="text-3xl font-black mb-1"></div>
                  <div className="text-slate-300 text-sm mb-4">Deployed to 12,000+ businesses</div>
                  <p className="text-sm text-slate-200 leading-relaxed">
                    We're the trusted choice for business owners who need capital fast, with full transparency.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Form */}
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="lg:col-span-3"
            >
              <div className="rounded-[32px] border border-gray-200 bg-white p-6 shadow-[0_14px_40px_rgba(15,23,42,0.07)] sm:p-8 md:p-10">
                {!submitted ? (
                  <>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Funding Application</h3>
                    <p className="mb-7 text-sm text-gray-500 md:mb-8">All fields marked * are required. Your information is secure and confidential.</p>

                    <form onSubmit={submit} className="space-y-6">
                      {/* Business Info */}
                      <div>
                        <h4 className="text-xs uppercase tracking-widest text-amber-600 font-semibold mb-4">Business Information</h4>
                        <div className="grid gap-4 sm:grid-cols-2">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">Business Name *</label>
                            <input
                              name="businessName"
                              value={form.businessName}
                              onChange={handle}
                              required
                              placeholder="Acme Co."
                              className={inputClass}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">Owner Name *</label>
                            <input
                              name="ownerName"
                              value={form.ownerName}
                              onChange={handle}
                              required
                              placeholder="Jane Smith"
                              className={inputClass}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Contact */}
                      <div>
                        <h4 className="text-xs uppercase tracking-widest text-amber-600 font-semibold mb-4">Contact Details</h4>
                        <div className="grid gap-4 sm:grid-cols-2">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">Email *</label>
                            <input
                              name="email"
                              type="email"
                              value={form.email}
                              onChange={handle}
                              required
                              placeholder="jane@acme.com"
                              className={inputClass}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone *</label>
                            <input
                              name="phone"
                              type="tel"
                              value={form.phone}
                              onChange={handle}
                              required
                              placeholder="(555) 000-0000"
                              className={inputClass}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Funding Details */}
                      <div>
                        <h4 className="text-xs uppercase tracking-widest text-amber-600 font-semibold mb-4">Funding Details</h4>
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">Industry *</label>
                            <select
                              name="industry"
                              value={form.industry}
                              onChange={handle}
                              required
                              className={`${inputClass} bg-white`}
                            >
                              <option value="">Select your industry</option>
                              {industries.map(i => <option key={i}>{i}</option>)}
                            </select>
                          </div>

                          <div className="grid gap-4 sm:grid-cols-2">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1.5">Monthly Revenue *</label>
                              <select
                                name="monthlyRevenue"
                                value={form.monthlyRevenue}
                                onChange={handle}
                                required
                                className={`${inputClass} bg-white`}
                              >
                                <option value="">Select range</option>
                                {revenues.map(r => <option key={r}>{r}</option>)}
                              </select>
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1.5">Funding Amount Needed *</label>
                              <select
                                name="fundingAmount"
                                value={form.fundingAmount}
                                onChange={handle}
                                required
                                className={`${inputClass} bg-white`}
                              >
                                <option value="">Select range</option>
                                {amounts.map(a => <option key={a}>{a}</option>)}
                              </select>
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">How will you use the funds? *</label>
                            <textarea
                              name="useOfFunds"
                              value={form.useOfFunds}
                              onChange={handle}
                              required
                              rows={4}
                              placeholder="e.g., Purchase inventory for the holiday season, hire 3 new staff members..."
                              className={`${inputClass} resize-none`}
                            />
                          </div>
                        </div>
                      </div>

                      {submitError && (
                        <p className="text-sm text-red-600">{submitError}</p>
                      )}

                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full rounded-full bg-[#0F172A] hover:bg-[#111c33] disabled:cursor-not-allowed disabled:opacity-60 text-white font-bold py-4 transition-all duration-300 text-base shadow-[0_12px_30px_rgba(15,23,42,0.16)] hover:shadow-[0_16px_36px_rgba(15,23,42,0.22)]"
                      >
                        {isSubmitting ? 'Sending...' : 'Submit Application →'}
                      </button>

                      <p className="text-xs text-gray-400 text-center">
                        By submitting, you agree to our Terms of Service and Privacy Policy. Applying does not affect your credit score.
                      </p>
                    </form>
                  </>
                ) : (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-16"
                  >
                    <div className="w-20 h-20 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-6 ring-1 ring-emerald-100">
                      <CheckCircle size={40} className="text-emerald-600" />
                    </div>
                    <h3 className="text-3xl font-bold text-gray-900 mb-3">Application Received!</h3>
                    <p className="text-gray-500 leading-relaxed max-w-sm mx-auto mb-4">
                      Thanks, <strong>{form.ownerName}</strong>! We've received your application for <strong>{form.businessName}</strong>.
                    </p>
                    <p className="text-gray-500 text-sm">
                      Expect a response at <strong>{form.email}</strong> within 4 business hours.
                    </p>
                  </motion.div>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
}
